import { createMiddleware } from "@tanstack/react-start";

const RELOAD_STORAGE_KEY = "wags:deploy-skew-reload";
const MISSING_SERVER_FN_PREFIX = "Server function info not found for ";
const DEPLOY_SKEW_MESSAGE_PREFIXES = [
    "Importing a module script failed",
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "Unable to preload CSS for ",
    MISSING_SERVER_FN_PREFIX,
];

type ReloadStorage = Pick<Storage, "getItem" | "setItem">;

export type DeploySkewReloader = { recover: () => boolean };

export function isDeploySkewMessage(message: string | undefined): boolean {
    return (
        message !== undefined &&
        DEPLOY_SKEW_MESSAGE_PREFIXES.some((prefix) =>
            message.startsWith(prefix),
        )
    );
}

export function createDeploySkewReloader(
    release: string,
    getStorage: () => ReloadStorage,
    reload: () => void,
): DeploySkewReloader {
    let reloading = false;
    return {
        recover() {
            if (reloading) return true;
            try {
                const storage = getStorage();
                if (storage.getItem(RELOAD_STORAGE_KEY) === release) {
                    return false;
                }
                storage.setItem(RELOAD_STORAGE_KEY, release);
            } catch {
                return false;
            }
            reloading = true;
            reload();
            return true;
        },
    };
}

const browserReloader = createDeploySkewReloader(
    import.meta.env.VITE_PUBLIC_SENTRY_RELEASE || "unknown-release",
    () => window.sessionStorage,
    () => window.location.reload(),
);

export function recoverFromDeploySkew(): boolean {
    if (typeof window === "undefined") return false;
    return browserReloader.recover();
}

export function installDeploySkewRecovery() {
    window.addEventListener("vite:preloadError", (event) => {
        if (recoverFromDeploySkew()) event.preventDefault();
    });
    window.addEventListener("unhandledrejection", (event) => {
        const reason: Error | null =
            event.reason instanceof Error ? event.reason : null;
        if (isDeploySkewMessage(reason?.message)) recoverFromDeploySkew();
    });
}

export const missingServerFnRequestMiddleware = createMiddleware({
    type: "request",
}).server(async ({ next }) => {
    try {
        return await next();
    } catch (error) {
        if (
            !(error instanceof Error) ||
            !error.message.startsWith(MISSING_SERVER_FN_PREFIX)
        ) {
            throw error;
        }
        return new Response(error.message, {
            status: 404,
            headers: { "content-type": "text/plain; charset=utf-8" },
        });
    }
});

export const deploySkewFunctionMiddleware = createMiddleware({
    type: "function",
}).client(async ({ next }) => {
    try {
        return await next();
    } catch (error) {
        if (error instanceof Error && isDeploySkewMessage(error.message))
            recoverFromDeploySkew();
        throw error;
    }
});
